/**
 * Report data extraction utilities
 *
 * Extracted from useAdminReports.js for testability.
 * Contains pure functions for extracting data from report arrays.
 */

import { t } from '../../i18n';

/**
 * Extract unique KPIs and locations from reports
 * @param {Array} reports - Array of report objects
 * @returns {{ validKPIs: Array, validLocations: Array }} Extracted values
 */
export function extractKPIsAndLocations(reports) {
  const validKPIs = [];
  const validLocations = [];

  reports.forEach((report) => {
    // Extract KPIs from array
    report.KPIs?.forEach((kpi) => {
      if (!validKPIs.includes(kpi)) validKPIs.push(kpi);
    });

    // Extract AI-detected KPI
    if (report.KPI_AI && !validKPIs.includes(report.KPI_AI)) {
      validKPIs.push(report.KPI_AI);
    }

    // Extract locations
    report.locations?.forEach((location) => {
      if (!validLocations.includes(location)) validLocations.push(location);
    });
  });

  return { validKPIs, validLocations };
}

/**
 * Build KPI filter options from valid KPIs and locations
 * @param {Array} validKPIs - Array of valid KPI values
 * @param {Array} validLocations - Array of valid location values
 * @returns {Array} Filter options for dropdown
 */
export function buildKPIFilterOptions(validKPIs, validLocations) {
  // Only the `label` is translated. `value`/`parent` are the canonical keys the
  // filter matches on, so they stay in English regardless of the UI language.
  const options = [
    { label: t('reports:filterPanel.hazardClassifications'), value: 'Hazard Classifications' },
  ];

  validKPIs.forEach((kpi) => {
    options.push({ label: kpi, value: kpi, parent: 'Hazard Classifications' });
  });

  options.push({ label: t('reports:filterPanel.locations'), value: 'Locations' });

  validLocations.forEach((location) => {
    options.push({ label: location, value: location, parent: 'Locations' });
  });

  return options;
}

/**
 * Extract unique HROs from reports
 * @param {Array} reports - Array of report objects
 * @returns {Array} Unique HRO values
 */
export function extractHROs(reports) {
  const hros = new Set();

  reports.forEach((report) => {
    report.HROs?.forEach((hro) => hros.add(hro));
    if (report.HRO_AI) {
      if (Array.isArray(report.HRO_AI)) {
        report.HRO_AI.forEach((hro) => hros.add(hro));
      } else {
        hros.add(report.HRO_AI);
      }
    }
  });

  return Array.from(hros);
}

/**
 * Count reports by status
 * @param {Array} reports - Array of report objects
 * @returns {{ open: number, closed: number, urgent: number, responded: number }}
 */
export function countReportsByStatus(reports) {
  let open = 0;
  let closed = 0;
  let urgent = 0;
  let responded = 0;

  reports.forEach((report) => {
    if (report.isClosed) {
      closed++;
    } else {
      open++;
    }

    if (report.isUrgent) {
      urgent++;
    }

    if (report.comments?.length > 0 && report.comments[0] !== 'No comments yet') {
      responded++;
    }
  });

  return { open, closed, urgent, responded };
}
